import { Button, Modal } from "@amzn/awsui-components-react";
import { CUSTOM_MODAL } from "helpers";
import { forwardRef, useImperativeHandle, useState } from "react";
import { ButtonProps, IconProps } from "@amzn/awsui-components-react";

/**
 * Reference to control the CustomModal component.
 * @interface
 * @property {() => void} close - Function to close the modal
 */
export interface CustomModalRef {
    close: () => void;
}

/**
 * Represents the props for the CustomModal component.
 * @interface
 * @property {ButtonProps.IconAlign} iconAlign - Alignment of the icon in the button
 * @property {IconProps.Name} iconName - Name of the icon to display in the button
 * @property {string} buttonContent - Text content of the button that triggers the modal
 * @property {ButtonProps.Variant} variant - Visual variant of the button
 * @property {boolean} disabled - Whether the button is disabled
 * @property {string} disabledReason - Reason why the button is disabled (for tooltip)
 * @property {React.ReactNode} modalFooter - Content to display in the modal footer
 * @property {React.ReactNode} modalHeader - Content to display in the modal header
 * @property {React.ReactNode} modalContent - Content to display in the modal body
 */
export interface CustomModalProps {
    iconAlign?: ButtonProps.IconAlign;
    iconName?: IconProps.Name;
    buttonContent?: string;
    variant?: ButtonProps.Variant;
    disabled?: boolean;
    disabledReason?: string;
    modalFooter?: React.ReactNode;
    modalHeader?: React.ReactNode;
    modalContent?: React.ReactNode;
}

/**
 * Component that generates a button that once clicked it opens a modal
 *
 * @property {ButtonProps.IconAlign} iconAlign - Alignment of the icon in the button
 * @property {IconProps.Name} iconName - Name of the icon to display in the button
 * @property {string} buttonContent - Text content of the button that triggers the modal
 * @property {ButtonProps.Variant} variant - Visual variant of the button
 * @property {boolean} disabled - Whether the button is disabled
 * @property {string} disabledReason - Reason why the button is disabled (for tooltip)
 * @property {React.ReactNode} modalFooter - Content to display in the modal footer
 * @property {React.ReactNode} modalHeader - Content to display in the modal header
 * @property {React.ReactNode} modalContent - Content to display in the modal body
 * @property {() => void} close - Function to close the modal
 *
 * @returns {JSX.Element} - The rendered content layout
 */
const CustomModal = forwardRef<CustomModalRef, CustomModalProps>(
    (
        {
            iconAlign,
            iconName,
            buttonContent,
            variant,
            disabled,
            disabledReason,
            modalFooter,
            modalHeader,
            modalContent,
        },
        ref,
    ) => {
        const [visible, setVisible] = useState(false);

        const setModalToVisible = () => setVisible(true);
        const setModalToInvisible = () => setVisible(false);

        useImperativeHandle(ref, () => ({
            close: setModalToInvisible,
        }));

        return (
            <>
                <Button
                    data-testid={CUSTOM_MODAL.OPEN_MODAL_BUTTON}
                    iconAlign={iconAlign}
                    iconName={iconName}
                    variant={variant}
                    onClick={setModalToVisible}
                    disabled={disabled}
                    disabledReason={disabledReason}
                    formAction="none"
                >
                    {buttonContent}
                </Button>
                <Modal onDismiss={setModalToInvisible} visible={visible} footer={modalFooter} header={modalHeader}>
                    {modalContent}
                </Modal>
            </>
        );
    },
);

export default CustomModal;
